/* Navigation model + route metadata shared by the sidebar and topbar. */
import {
  LayoutDashboard,
  ShoppingBag,
  FileBarChart2,
  ClipboardList,
  Users,
  Package,
  Truck,
  Archive,
  // ScanLine, // re-enable with the Pack & ship nav entry
  Receipt,
} from "lucide-react";

/* Grouped sidebar navigation. `icon` holds a lucide component reference. */
export const NAV_GROUPS = [
  {
    group: "Overview",
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        path: "/dashBoard",
      },
    ],
  },
  {
    group: "Operations",
    items: [
      {
        id: "orders",
        label: "Orders",
        icon: ShoppingBag,
        children: [
          { label: "CHE / Engage", path: "/cheOrders" },
          { label: "B2C", path: "/b2cOrders" },
          // B2B Partners screens disabled — re-enable to expose B2B orders nav.
          // { label: "B2B Partners", path: "/b2bOrders" },
          { label: "Eyewear", path: "/eyewearOrders" },
        ],
      },
      {
        id: "reports",
        label: "Reports",
        icon: FileBarChart2,
        children: [
          { label: "Enquiry report", path: "/enquiry" },
          { label: "Order report", path: "/orderReport" },
          { label: "Consignment report", path: "/consignment/che" },
        ],
      },
      /* GRN template removed from the Operations panel. Inward now happens on
         Supply › Purchase › GRN · Inward, which posts to stock; this screen was the
         older template-based flow. The route and component are still there, so a
         bookmarked link keeps working — uncomment to put it back in the nav. */
      // { id: "grn", label: "GRN template", icon: ClipboardList, path: "/grnTemplate" },
    ],
  },
  {
    group: "Supply",
    items: [
      {
        id: "purchase",
        label: "Purchase",
        icon: Truck,
        children: [
          { label: "Purchase Orders", path: "/purchaseOrders" },
          // GRN = the Goods Receipt / Direct-Purchase screen (receive goods → stock).
          { label: "GRN · Inward", path: "/grn" },
          { label: "Purchase Return", path: "/purchase-returns" },
          { label: "Purchase Invoice", path: "/purchase-invoices" },
          { label: "Suppliers", path: "/suppliers" },
        ],
      },
      {
        id: "sales",
        label: "Sales",
        icon: Receipt,
        children: [
          // Bill Entry = manual counter-sale billing (walk-in customer).
          { label: "Bill Entry", path: "/salesBillEntry" },
          // Bill History = the existing sales-invoice list (Billing screen).
          { label: "Bill History", path: "/invoices" },
          // Online Orders = the former Allocation & Billing (fulfil patient orders).
          { label: "Online Orders", path: "/allocationBilling" },
          { label: "Sales Return", path: "/sales-returns" },
          // Doctors = prescribing-doctor master, used when creating sales bills.
          { label: "Doctors", path: "/doctors" },
        ],
      },
      {
        id: "inventory",
        label: "Inventory",
        icon: Package,
        path: "/inventory",
      },
      {
        id: "supplyReports",
        label: "Reports",
        icon: FileBarChart2,
        children: [
          /* Overview = the store dashboard: stock value, and sales / purchase /
             profit by day. First because it is where you look before drilling in. */
          { label: "Overview", path: "/reports/overview" },
          { label: "Stock Reports", path: "/reports/stock" },
          { label: "Sales Reports", path: "/reports/sales" },
          { label: "Purchase Reports", path: "/reports/purchase" },
        ],
      },
    ],
  },
  {
    group: "Master",
    items: [
      {
        id: "customers",
        label: "Customers",
        icon: Users,
        children: [
          { label: "Patients", path: "/profile" },
          // B2B Partners screens disabled — re-enable to expose B2B partner master nav.
          // { label: "B2B partners", path: "/b2bKycReport" },
        ],
      },
      {
        id: "medicineRequests",
        label: "Medicine Requests",
        icon: ClipboardList,
        children: [
          // Requesters raise + track here; the master-data team decides in Approvals.
          { label: "My Requests", path: "/master/medicine-requests" },
          /* Deciding a request is an admin action, so the entry is hidden from
             plain PH users — sideNav drops adminOnly children, and the route
             itself is guarded (see RequireAdmin in Route/routing.jsx). */
          {
            label: "Approvals",
            path: "/master/medicine-requests/approvals",
            adminOnly: true,
          },
        ],
      },
      /* Racks = the storage-location master: a store's shelves, and the shelf
         each medicine lives on (which prefills the rack on every GRN line).
         Open to every pharmacy user, unlike the approval queue above: racking out
         a store is floor work done by whoever receives the goods, not a
         master-data decision — and the GRN already lets any user create a rack
         inline, so gating the master screen would only hide the tidy-up. Each
         user sees their own store; a head can switch. */
      { id: "racks", label: "Racks", icon: Archive, path: "/master/racks" },
    ],
  },
];

/* Exact-path → breadcrumb / title metadata. */
const ROUTE_META = {
  "/dashBoard": { title: "Dashboard", crumbs: ["Dashboard"] },
  "/cheOrders": {
    title: "CHE / Engage orders",
    crumbs: ["Orders", "CHE / Engage"],
  },
  "/b2cOrders": { title: "B2C orders", crumbs: ["Orders", "B2C"] },
  // B2B Partners screens disabled — route meta kept commented for easy restore.
  // "/b2bOrders": { title: "B2B partner orders", crumbs: ["Orders", "B2B Partners"] },
  "/eyewearOrders": { title: "Eyewear orders", crumbs: ["Orders", "Eyewear"] },
  "/enquiry": {
    title: "Enquiry report",
    crumbs: ["Reports", "Enquiry report"],
  },
  "/orderReport": {
    title: "Order report",
    crumbs: ["Reports", "Order report"],
  },
  // "/b2bOrderReport": { title: "B2B order report", crumbs: ["Reports", "Order report", "B2B"] },
  "/b2cOrderReport": {
    title: "B2C order report",
    crumbs: ["Reports", "Order report", "B2C"],
  },
  "/eyewearOrderReport": {
    title: "Eyewear order report",
    crumbs: ["Reports", "Order report", "Eyewear"],
  },
  "/grnTemplate": { title: "GRN template", crumbs: ["GRN template"] },
  "/purchaseOrders": {
    title: "Purchase Orders",
    crumbs: ["Supply", "Purchase", "Purchase Orders"],
  },
  "/suppliers": {
    title: "Suppliers",
    crumbs: ["Supply", "Purchase", "Suppliers"],
  },
  "/purchase-returns": {
    title: "Purchase Return",
    crumbs: ["Supply", "Purchase", "Purchase Return"],
  },
  "/purchase-invoices": {
    title: "Purchase Invoice",
    crumbs: ["Supply", "Purchase", "Purchase Invoice"],
  },
  // GRN receive screen — reached from a PO, not in the nav.
  "/grn": { title: "GRN · Inward", crumbs: ["Supply", "Purchase", "GRN"] },
  "/salesBillEntry": {
    title: "Bill Entry",
    crumbs: ["Supply", "Sales", "Bill Entry"],
  },
  "/allocationBilling": {
    title: "Online Orders",
    crumbs: ["Supply", "Sales", "Online Orders"],
  },
  // "/packShip": { title: "Pack & ship", crumbs: ["Supply", "Sales", "Pack & ship"] }, // disabled for now
  "/invoices": {
    title: "Bill History",
    crumbs: ["Supply", "Sales", "Bill History"],
  },
  "/sales-returns": {
    title: "Sales Return",
    crumbs: ["Supply", "Sales", "Sales Return"],
  },
  "/doctors": { title: "Doctors", crumbs: ["Supply", "Sales", "Doctors"] },
  "/inventory": { title: "Inventory", crumbs: ["Supply", "Inventory"] },
  "/reports/overview": {
    title: "Overview",
    crumbs: ["Supply", "Reports", "Overview"],
  },
  "/reports/stock": {
    title: "Stock Reports",
    crumbs: ["Supply", "Reports", "Stock Reports"],
  },
  "/reports/stock/batchwise": {
    title: "Batchwise Stock Report",
    crumbs: ["Supply", "Reports", "Stock Reports", "Batchwise"],
  },
  "/reports/stock/expiry": {
    title: "Expiry Report",
    crumbs: ["Supply", "Reports", "Stock Reports", "Expiry"],
  },
  "/reports/stock/low": {
    title: "Low Stock Report",
    crumbs: ["Supply", "Reports", "Stock Reports", "Low stock"],
  },
  "/reports/stock/out-of-stuck": {
    title: "Out of Stuck Report",
    crumbs: ["Supply", "Reports", "Stock Reports", "Out of stuck"],
  },
  "/reports/stock/snapshot": {
    title: "Inventory Snapshot",
    crumbs: ["Supply", "Reports", "Stock Reports", "Inventory snapshot"],
  },
  "/reports/sales": {
    title: "Sales Reports",
    crumbs: ["Supply", "Reports", "Sales Reports"],
  },
  "/reports/sales/register": {
    title: "Sales Register",
    crumbs: ["Supply", "Reports", "Sales Reports", "Register"],
  },
  "/reports/sales/channel": {
    title: "Sales by Channel",
    crumbs: ["Supply", "Reports", "Sales Reports", "By channel"],
  },
  "/reports/sales/outstanding": {
    title: "Outstanding Payments",
    crumbs: ["Supply", "Reports", "Sales Reports", "Outstanding"],
  },
  "/reports/purchase": {
    title: "Purchase Reports",
    crumbs: ["Supply", "Reports", "Purchase Reports"],
  },
  "/reports/purchase/register": {
    title: "Purchase Register",
    crumbs: ["Supply", "Reports", "Purchase Reports", "Register"],
  },
  "/reports/purchase/supplier": {
    title: "Supplier-wise Purchase",
    crumbs: ["Supply", "Reports", "Purchase Reports", "By supplier"],
  },
  "/reports/purchase/orders": {
    title: "Purchase Orders",
    crumbs: ["Supply", "Reports", "Purchase Reports", "Orders"],
  },
  "/profile": { title: "Patients", crumbs: ["Customers", "Patients"] },
  "/master/medicine-requests": {
    title: "Medicine Requests",
    crumbs: ["Master", "Medicine Requests"],
  },
  "/master/medicine-requests/approvals": {
    title: "Medicine Request Approvals",
    crumbs: ["Master", "Medicine Requests", "Approvals"],
  },
  "/master/racks": { title: "Racks", crumbs: ["Master", "Racks"] },
  // "/b2bKycReport": { title: "B2B partners", crumbs: ["Customers", "B2B partners"] },
};

/* Prefix-matched routes (dynamic segments / nested paths). */
const ROUTE_PREFIXES = [
  {
    prefix: "/consignment",
    title: "Consignment report",
    crumbs: ["Reports", "Consignment report"],
  },
  // B2B Partners screens disabled — re-enable for /b2bProfile/:id breadcrumb.
  // { prefix: "/b2bProfile", title: "B2B partner", crumbs: ["Customers", "B2B partners", "Profile"] },
];

export function getRouteMeta(pathname = "") {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];
  const hit = ROUTE_PREFIXES.find((r) => pathname.startsWith(r.prefix));
  if (hit) return { title: hit.title, crumbs: hit.crumbs };
  return { title: "CureBay", crumbs: ["CureBay"] };
}
